import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, AlertTriangle, Loader2, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import Card from '@/components/ui/Card';

type PlanId = 'starter' | 'growth' | 'pro';

interface WorkspaceRow {
  id: string;
  name: string;
  slug: string;
  plan_id: PlanId;
  owner_email: string | null;
  created_at: string;
  has_active_subscription: boolean;
  subscription_plan_id: PlanId | null;
}

export default function PlatformWorkspaces() {
  const user = useAuthStore((s) => s.user);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.isPlatformAdmin) return;
    api.get<{ workspaces: WorkspaceRow[] }>('/admin/platform/settings/workspaces')
      .then((res) => setWorkspaces(res.workspaces))
      .catch((err) => setError(err?.message || 'Failed to load workspaces'))
      .finally(() => setLoading(false));
  }, [user?.isPlatformAdmin]);

  if (!user?.isPlatformAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-900 font-medium">Platform admin access required.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-8">
      <div className="flex items-center gap-3 mb-2">
        <CreditCard className="w-7 h-7 text-sage-700" />
        <h1 className="text-2xl font-bold text-stone-900">Workspaces</h1>
      </div>
      <p className="text-stone-500 mb-8">
        Every Rumio customer (paying or otherwise) appears here. Click into one to manage its
        subscription tier override and other per-workspace settings.
      </p>

      {loading && <Loader2 className="w-5 h-5 animate-spin text-stone-400" />}

      {error && (
        <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-sm text-red-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && workspaces.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-stone-600">No workspaces yet.</p>
          <p className="text-sm text-stone-500 mt-2">
            Workspaces are created when a user signs up via the SaaS flow at rumio.world, or via{' '}
            <span className="font-mono">POST /api/platform/workspaces</span>.
          </p>
        </Card>
      )}

      {!loading && workspaces.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-stone-500 border-b border-stone-200">
                <th className="px-5 py-3 font-medium">Workspace</th>
                <th className="px-5 py-3 font-medium">Owner</th>
                <th className="px-5 py-3 font-medium">Tier</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map((w) => (
                <tr
                  key={w.id}
                  className="border-b border-stone-100 last:border-0 hover:bg-stone-50"
                >
                  <td className="px-5 py-3">
                    <Link to={`/admin/platform/workspaces/${w.id}`} className="block">
                      <div className="font-medium text-stone-900">{w.name}</div>
                      <div className="text-xs text-stone-500">{w.slug}</div>
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-stone-600">{w.owner_email || '—'}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-700 capitalize">
                      {w.plan_id}
                    </span>
                    {w.has_active_subscription && (
                      <div className="text-xs text-amber-700 mt-1">
                        Stripe active · {w.subscription_plan_id}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link
                      to={`/admin/platform/workspaces/${w.id}`}
                      className="inline-flex items-center text-sage-700 hover:text-sage-800"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
